import { jwtDecode } from 'jwt-decode';
import { getActiveToken } from '@/common/helper/tokenStorage';

/** A recipient choice in a step's contact dropdown. */
export interface ContactRoleOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface AddedRole {
  key: string;
  label: string;
  /** Listed right after this role; left out when that role isn't offered. */
  after: string;
  /** Needs the active execution leg: customer/execution leg companies only. */
  requiresCustomerLeg?: boolean;
}

/**
 * Recipient roles LogiBlocks offers on top of the shared additional-recipient list
 * (which Notifications also uses, so they are added here rather than there). The
 * backend resolves them in logiblocks/graph/contact_utils.py.
 */
const ADDED_ROLES: AddedRole[] = [
  {
    key: 'TRANSPORT_PROVIDER_SECONDARY_CONTACT',
    label: 'Transport provider secondary contact',
    after: 'TRANSPORT_PROVIDER_PRIMARY_CONTACT',
  },
  {
    key: 'ORIGIN_SECONDARY_CONTACT',
    label: 'Origin secondary contact',
    after: 'ORIGIN_PRIMARY_CONTACT',
  },
  {
    key: 'DESTINATION_SECONDARY_CONTACT',
    label: 'Destination secondary contact',
    after: 'DESTINATION_PRIMARY_CONTACT',
  },
  {
    key: 'CONTROLLING_OFFICE_SECONDARY_CONTACT',
    label: 'Controlling office secondary contact',
    after: 'CONTROLLING_OFFICE_PRIMARY_CONTACT',
  },
  {
    key: 'PICKUP_OFFICE_PRIMARY_CONTACT',
    label: 'Pickup office (Pickup RTG) primary contact',
    after: 'CONTROLLING_OFFICE_SECONDARY_CONTACT',
    requiresCustomerLeg: true,
  },
  {
    key: 'PICKUP_OFFICE_SECONDARY_CONTACT',
    label: 'Pickup office (Pickup RTG) secondary contact',
    after: 'PICKUP_OFFICE_PRIMARY_CONTACT',
    requiresCustomerLeg: true,
  },
  {
    key: 'DROP_OFFICE_PRIMARY_CONTACT',
    label: 'Drop office (Drop RTG) primary contact',
    after: 'PICKUP_OFFICE_SECONDARY_CONTACT',
    requiresCustomerLeg: true,
  },
  {
    key: 'DROP_OFFICE_SECONDARY_CONTACT',
    label: 'Drop office (Drop RTG) secondary contact',
    after: 'DROP_OFFICE_PRIMARY_CONTACT',
    requiresCustomerLeg: true,
  },
];

/** Every role key LogiBlocks adds, for code that must treat them as shipment roles. */
export const ADDED_CONTACT_ROLE_KEYS: ReadonlySet<string> = new Set(
  ADDED_ROLES.map((role) => role.key)
);

// The shared list calls it "Controlling office"; next to its secondary it needs the
// word "primary". Only the LogiBlocks dropdowns rename it.
const LABEL_OVERRIDES: Record<string, string> = {
  CONTROLLING_OFFICE_PRIMARY_CONTACT: 'Controlling office primary contact',
};

/** Whether the signed-in company has customer/execution legs (the `enablecustomerleg`
 * token claim, as metaVariableCatalog reads it for the active-leg variables). */
export function customerLegEnabled(): boolean {
  const token = getActiveToken();
  if (!token) return false;
  try {
    return Boolean(
      jwtDecode<{ enablecustomerleg?: boolean }>(token).enablecustomerleg
    );
  } catch {
    return false;
  }
}

/**
 * Adds LogiBlocks' secondary-contact and pickup/drop office roles to the role options
 * from the shared list, each right after the role it extends. A role whose anchor
 * isn't offered (e.g. the transport provider in Email) is left out; pickup and drop
 * offices only appear for customer/execution leg companies. Other options keep their
 * order after the roles.
 */
export function withLogiblocksContactRoles(
  options: readonly ContactRoleOption[],
  { customerLeg }: { customerLeg: boolean }
): ContactRoleOption[] {
  const result: ContactRoleOption[] = options.map((option) =>
    LABEL_OVERRIDES[option.value]
      ? { ...option, label: LABEL_OVERRIDES[option.value] }
      : option
  );
  for (const role of ADDED_ROLES) {
    if (role.requiresCustomerLeg && !customerLeg) continue;
    const anchorIndex = result.findIndex(
      (option) => option.value === role.after
    );
    if (anchorIndex === -1) continue;
    const anchor = result[anchorIndex];
    result.splice(anchorIndex + 1, 0, {
      value: role.key,
      label: role.label,
      disabled: anchor.disabled,
    });
  }
  return result;
}
